import React from 'react';

// Hand-drawn inline SVG thumbnails. Tiny (~600 bytes each), no external assets.
// Each one is a 200×130 mockup illustrating the layout idea so admins can
// pick visually instead of reading variant names.

const ThumbFrame = ({ children, bg = 'linear-gradient(135deg,#FFDEE9,#FFF4D6,#B5FFFC)' }) => (
  <svg viewBox="0 0 200 130" xmlns="http://www.w3.org/2000/svg" className="block w-full h-auto rounded-md">
    <defs>
      <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FFDEE9" />
        <stop offset="50%" stopColor="#FFF4D6" />
        <stop offset="100%" stopColor="#B5FFFC" />
      </linearGradient>
    </defs>
    <rect width="200" height="130" rx="6" fill="url(#bg)" />
    {children}
  </svg>
);

const ThumbFlash = () => (
  <ThumbFrame>
    {/* Animated word lines */}
    <rect x="14" y="32" width="64" height="9" rx="2" fill="#FF3B6B" />
    <rect x="14" y="46" width="80" height="9" rx="2" fill="#06D6A0" />
    <rect x="14" y="60" width="56" height="9" rx="2" fill="#FFD166" />
    <text x="100" y="68" fontSize="14">🎉</text>
    {/* CTA pills */}
    <rect x="14" y="84" width="48" height="14" rx="7" fill="#FF3B6B" />
    <rect x="68" y="84" width="40" height="14" rx="7" fill="#fff" stroke="#1E1E24" strokeOpacity="0.15" />
    {/* Confetti dots */}
    {[[120, 30], [148, 50], [170, 25], [160, 80], [134, 100], [180, 95]].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r="2.5" fill={['#FF3B6B', '#06D6A0', '#FFD166'][i % 3]} />
    ))}
  </ThumbFrame>
);

const ThumbCelebration = () => (
  <ThumbFrame>
    {/* Photo block */}
    <rect x="0" y="0" width="200" height="130" fill="#1E1E24" opacity="0.5" />
    <rect x="0" y="0" width="200" height="130" fill="url(#bg)" opacity="0.4" />
    <rect x="20" y="36" width="120" height="10" rx="2" fill="#FFF" opacity="0.95" />
    <rect x="20" y="52" width="90" height="8" rx="2" fill="#FFF" opacity="0.7" />
    <rect x="20" y="78" width="40" height="14" rx="7" fill="#FF3B6B" />
    {/* Testimonial badge */}
    <rect x="142" y="78" width="48" height="32" rx="4" fill="#FFF" />
    <circle cx="152" cy="92" r="3" fill="#FFD166" />
    <rect x="158" y="89" width="26" height="3" rx="1" fill="#1E1E24" />
    <rect x="158" y="95" width="18" height="3" rx="1" fill="#6B6B7A" />
  </ThumbFrame>
);

const ThumbSplit = () => (
  <ThumbFrame>
    {/* Left copy */}
    <rect x="14" y="36" width="80" height="10" rx="2" fill="#1E1E24" />
    <rect x="14" y="52" width="64" height="8" rx="2" fill="#6B6B7A" />
    <rect x="14" y="64" width="74" height="8" rx="2" fill="#6B6B7A" />
    <rect x="14" y="86" width="44" height="14" rx="7" fill="#FF3B6B" />
    {/* Right photo card */}
    <rect x="110" y="20" width="78" height="90" rx="6" fill="#FFF" stroke="#1E1E24" strokeOpacity="0.1" />
    <rect x="116" y="26" width="66" height="68" rx="3" fill="#FFD166" />
    <circle cx="149" cy="58" r="14" fill="#FF3B6B" />
    <text x="172" y="90" fontSize="9">✨</text>
  </ThumbFrame>
);

const ThumbMascot = () => (
  <ThumbFrame>
    {/* Left copy */}
    <rect x="14" y="32" width="80" height="11" rx="2" fill="#FF3B6B" />
    <rect x="14" y="48" width="68" height="8" rx="2" fill="#6B6B7A" />
    <rect x="14" y="60" width="74" height="8" rx="2" fill="#6B6B7A" />
    <rect x="14" y="82" width="44" height="14" rx="7" fill="#06D6A0" />
    {/* Mascot bubble */}
    <circle cx="150" cy="68" r="36" fill="#FFD166" opacity="0.65" />
    <circle cx="150" cy="68" r="28" fill="#FFF" />
    <text x="138" y="78" fontSize="26">🦄</text>
  </ThumbFrame>
);

const ThumbVideo = () => (
  <ThumbFrame>
    {/* Dark overlay */}
    <rect x="0" y="0" width="200" height="130" fill="#0F2A44" opacity="0.7" />
    {/* Play icon */}
    <circle cx="100" cy="55" r="14" fill="#fff" opacity="0.92" />
    <polygon points="95,49 95,61 107,55" fill="#FF3B6B" />
    <rect x="40" y="80" width="120" height="9" rx="2" fill="#FFF" />
    <rect x="56" y="94" width="88" height="6" rx="2" fill="#FFF" opacity="0.75" />
    <rect x="78" y="108" width="44" height="12" rx="6" fill="#FF3B6B" />
  </ThumbFrame>
);

const ThumbMinimalist = () => (
  <ThumbFrame>
    {/* Full-bleed muted bg */}
    <rect x="0" y="0" width="200" height="130" fill="#1E1E24" opacity="0.35" />
    <rect x="0" y="0" width="200" height="130" fill="url(#bg)" opacity="0.6" />
    <rect x="16" y="14" width="36" height="6" rx="2" fill="#FFF" opacity="0.85" />
    <rect x="16" y="40" width="140" height="14" rx="2" fill="#FFF" />
    <rect x="16" y="60" width="100" height="14" rx="2" fill="#FFF" />
    <rect x="16" y="92" width="48" height="14" rx="7" fill="#FF3B6B" />
  </ThumbFrame>
);

const ThumbStoryCards = () => (
  <ThumbFrame>
    {/* Left copy */}
    <rect x="12" y="34" width="74" height="10" rx="2" fill="#1E1E24" />
    <rect x="12" y="50" width="60" height="7" rx="2" fill="#6B6B7A" />
    <rect x="12" y="78" width="42" height="14" rx="7" fill="#FF3B6B" />
    {/* 2x2 polaroid grid */}
    <g transform="translate(108,16)">
      <rect x="0"  y="0"  width="36" height="44" rx="3" fill="#FFEFF5" transform="rotate(-4 18 22)" stroke="#fff" strokeWidth="2" />
      <rect x="40" y="0"  width="36" height="44" rx="3" fill="#EDFFF8" transform="rotate(3 58 22)"  stroke="#fff" strokeWidth="2" />
      <rect x="0"  y="48" width="36" height="44" rx="3" fill="#FFF8E7" transform="rotate(4 18 70)"  stroke="#fff" strokeWidth="2" />
      <rect x="40" y="48" width="36" height="44" rx="3" fill="#EAF1FF" transform="rotate(-3 58 70)" stroke="#fff" strokeWidth="2" />
    </g>
  </ThumbFrame>
);

export const HERO_VARIANT_DEFINITIONS = [
  { value: 'flash',        label: 'Flash',        tagline: 'Animated rotating words + confetti (default)',          Thumb: ThumbFlash },
  { value: 'celebration',  label: 'Celebration',  tagline: 'Faded photo background + testimonial badge',             Thumb: ThumbCelebration },
  { value: 'split',        label: 'Split',        tagline: 'Copy on the left, large photo on the right',             Thumb: ThumbSplit },
  { value: 'mascot',       label: 'Mascot',       tagline: 'Friendly cartoon mascot beside the copy',                Thumb: ThumbMascot },
  { value: 'video',        label: 'Video',        tagline: 'Auto-loop muted background video with overlay',          Thumb: ThumbVideo },
  { value: 'minimalist',   label: 'Minimalist',   tagline: 'Full-bleed photo + bold short headline',                 Thumb: ThumbMinimalist },
  { value: 'story_cards',  label: 'Story Cards',  tagline: 'Polaroid collage of past parties',                       Thumb: ThumbStoryCards },
];
